import { useEffect, useMemo, useState, type ReactNode } from 'react';

/** The public booking page (REQ-C03, C06, C16, C25, C26).
 *
 *  No login. The token is read from the URL fragment and sent in a POST body, so it
 *  never travels in a request line. The page shows only what the server's public
 *  model carries: the clinic, the organiser's first name, script titles, masked
 *  invitees and free times. Arabic first for an Arabic browser, with a toggle.
 */

type Lang = 'ar' | 'en';
interface Invitee { index: number; label: string; kind: string }
interface Booking {
  clinic: string; organizer: string; scripts: string[]; invitees: Invitee[];
  state: 'open' | 'booked' | 'closed'; minutes: number; tz: string; slots: string[];
  starts_at?: string | null; ends_at?: string | null; meet_uri?: string | null; can_change: boolean; suggested: boolean;
  // general meetings (2026-09-27)
  purpose?: 'script_call' | 'meeting'; title?: string | null; team?: string[]; with_meet?: boolean; recorded?: boolean;
  location?: string | null; change_cutoff_hours?: number;
}

/** A general meeting's words: the same page, about a meeting rather than scripts. */
const M = {
  ar: {
    title: 'اختيار موعد اجتماعنا', with: (names: string) => `مع ${names} من KAF PRO`, agenda: 'ما سنناقشه',
    booked: 'حُجِز الاجتماع', cancel: 'إلغاء الاجتماع', sure: 'هل تريدون إلغاء الاجتماع؟', yes: 'نعم، إلغاء الاجتماع',
    cancelled: 'أُلغي الاجتماع.', inPerson: (m: number, where: string) => `${m} دقيقة، في ${where}`,
    disclosure: 'يُسجَّل الاجتماع ويُفرَّغ نصيًّا على خوادم KAF PRO الخاصة، ليبقى ما نتفق عليه موثّقًا كما قيل.',
  },
  en: {
    title: 'Choose a time for our meeting', with: (names: string) => `with ${names} from KAF PRO`, agenda: 'What we will discuss',
    booked: 'Your meeting is booked', cancel: 'Cancel the meeting', sure: 'Cancel the meeting?', yes: 'Yes, cancel it',
    cancelled: 'The meeting has been cancelled.', inPerson: (m: number, where: string) => `${m} minutes, at ${where}`,
    disclosure: "The meeting is recorded and transcribed on KAF PRO's own servers, so what we agree stays on record exactly as it was said.",
  },
};
const R = {
  ar: { why: 'سبب الإلغاء', whyPh: 'يساعدنا ذلك على ترتيب موعد آخر', late: (h: number) => `لتغيير الموعد أو إلغائه قبل أقل من ${h === 2 ? 'ساعتين' : `${h} ساعات`} من بدايته، يُرجى مراسلة KAF PRO.` },
  en: { why: 'Why are you cancelling?', whyPh: 'It helps us find another time', late: (h: number) => `Less than ${h} hours to go: to move or cancel, please message KAF PRO.` },
};

const T = {
  ar: {
    title: 'اختيار موعد مكالمة مراجعة النصوص', with: (o: string) => `مع ${o} من KAF PRO`, scripts: 'النصوص التي سنراجعها',
    who: 'مَن يحجز الموعد؟', pick: 'اختاروا الوقت (بتوقيت دبي)', yours: 'بتوقيتكم', confirm: 'تأكيد الموعد',
    none: 'لا يناسبنا أيٌّ من هذه المواعيد', suggestPh: 'مثلًا: الخميس بعد السابعة مساءً', send: 'إرسال',
    thanks: 'شكرًا لكم، وصلنا اقتراحكم وسنتواصل معكم.', booked: 'حُجِزت المكالمة', join: 'الانضمام عبر Google Meet',
    invite: 'ستصلكم دعوة التقويم عبر البريد الإلكتروني.', move: 'تغيير الموعد', cancel: 'إلغاء المكالمة',
    sure: 'هل تريدون إلغاء المكالمة؟', yes: 'نعم، إلغاء المكالمة', back: 'رجوع', cancelled: 'أُلغيت المكالمة.',
    gone: 'لم يعد هذا الرابط صالحًا. يُرجى طلب رابط جديد من KAF PRO.', loading: 'جارٍ التحميل...',
    noSlots: 'لا توجد مواعيد متاحة في الأيام القادمة.', minutes: (m: number) => `${m} دقيقة عبر Google Meet`,
    disclosure: 'تُسجَّل المكالمة وتُفرَّغ نصيًّا على خوادم KAF PRO الخاصة، لتصل ملاحظاتكم إلى الفريق كما قلتموها تمامًا.',
    readScripts: 'يمكنكم قراءة النصوص كاملة في بوابة KAF PRO‏.', other: 'English', err: 'تعذّر إتمام الطلب. يُرجى المحاولة مرة أخرى.',
  },
  en: {
    title: 'Choose a time for your script call', with: (o: string) => `with ${o} from KAF PRO`, scripts: 'Scripts we will go through',
    who: 'Who is booking?', pick: 'Pick a time (Dubai time)', yours: 'Your time', confirm: 'Confirm',
    none: 'None of these times work', suggestPh: 'For example: Thursday after 7 pm', send: 'Send',
    thanks: 'Thank you, we have your suggestion and will get back to you.', booked: 'Your call is booked', join: 'Join on Google Meet',
    invite: 'A calendar invitation is on its way by email.', move: 'Move the call', cancel: 'Cancel the call',
    sure: 'Cancel the call?', yes: 'Yes, cancel it', back: 'Back', cancelled: 'The call has been cancelled.',
    gone: 'This link is no longer valid. Please ask KAF PRO for a new one.', loading: 'Loading...',
    noSlots: 'No times are free in the coming days.', minutes: (m: number) => `${m} minutes on Google Meet`,
    disclosure: "The call is recorded and transcribed on KAF PRO's own servers, so your notes reach the team exactly as you said them.",
    readScripts: 'You can read the full scripts in the KAF PRO portal.', other: 'العربية', err: 'That did not go through. Please try again.',
  },
};

function tokenFromHash(): string {
  const m = /[#&]t=([A-Za-z0-9_-]+)/.exec(window.location.hash);
  return m ? m[1] : '';
}

async function post<R>(path: string, body: object): Promise<{ ok: boolean; status: number; data: R | { detail?: string } }> {
  const r = await fetch(`/api/book/${path}`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
    credentials: 'omit', referrerPolicy: 'no-referrer' });
  let data: R | { detail?: string } = {};
  try { data = await r.json(); } catch { /* empty body */ }
  return { ok: r.ok, status: r.status, data };
}

export default function BookPage() {
  const token = useMemo(tokenFromHash, []);
  const [lang, setLang] = useState<Lang>(() => (navigator.language || '').toLowerCase().startsWith('ar') ? 'ar' : 'en');
  const t = T[lang];
  const [b, setB] = useState<Booking | null>(null);
  const [gone, setGone] = useState(false);
  const [who, setWho] = useState<number | null>(null);
  const [slot, setSlot] = useState<string | null>(null);
  const [moving, setMoving] = useState(false);
  const [asking, setAsking] = useState(false);
  const [suggest, setSuggest] = useState('');
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [busy, setBusy] = useState(false);
  const [day, setDay] = useState(0);
  const [error, setError] = useState('');
  const [reason, setReason] = useState('');

  // The token stays in the fragment: browsers never send a fragment to any server, and
  // keeping it means a doctor who refreshes the page still has a working link.
  useEffect(() => {
    if (!token) { setGone(true); return; }
    post<Booking>('lookup', { token }).then((r) => {
      if (!r.ok) { setGone(true); return; }
      const data = r.data as Booking;
      setB(data);
      if (data.invitees.length === 1) setWho(data.invitees[0].index);
    });
  }, [token]);

  useEffect(() => {
    document.documentElement.lang = lang;
    document.documentElement.dir = lang === 'ar' ? 'rtl' : 'ltr';
    return () => { document.documentElement.dir = 'ltr'; document.documentElement.lang = 'en'; };
  }, [lang]);

  const loc = lang === 'ar' ? 'ar-AE-u-nu-latn' : 'en-AE';
  const localTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const sameZone = new Date().toLocaleString('en-US', { timeZone: localTz }) === new Date().toLocaleString('en-US', { timeZone: 'Asia/Dubai' });
  const dayKey = (iso: string) => new Date(iso).toLocaleDateString('en-CA', { timeZone: 'Asia/Dubai' });
  const dayLabel = (iso: string) => new Date(iso).toLocaleDateString(loc, { timeZone: 'Asia/Dubai', weekday: 'long', day: 'numeric', month: 'long' });
  const time = (iso: string, tz = 'Asia/Dubai') => new Date(iso).toLocaleTimeString(loc, { timeZone: tz, hour: '2-digit', minute: '2-digit' });
  const full = (iso: string) => `${dayLabel(iso)}, ${time(iso)}`;

  const days = useMemo(() => {
    const m = new Map<string, string[]>();
    for (const s of b?.slots ?? []) { const k = dayKey(s); m.set(k, [...(m.get(k) ?? []), s]); }
    return [...m.values()].slice(0, 10);
  }, [b]);

  async function act<R>(path: string, body: object) {
    setBusy(true); setError('');
    const r = await post<R>(path, { token, ...body });
    setBusy(false);
    if (r.status === 404) { setGone(true); return null; }
    if (!r.ok) { setError((r.data as { detail?: string }).detail || t.err); return null; }
    return r.data as R;
  }

  const shell = (children: ReactNode) => (
    <div className="min-h-screen bg-slate-50 text-slate-900" style={{ fontFamily: "'Segoe UI', Tahoma, Arial, sans-serif" }}>
      <div className="max-w-xl mx-auto px-4 py-6 sm:py-10">
        <div className="flex items-center justify-between mb-6">
          <div className="text-[11px] tracking-[0.2em] text-slate-500">KAF PRO</div>
          <button type="button" onClick={() => setLang(lang === 'ar' ? 'en' : 'ar')} className="text-xs text-sky-800 underline" data-testid="lang-toggle">{t.other}</button>
        </div>
        {children}
        {b?.purpose !== 'meeting' && <p className="text-[12px] leading-relaxed text-slate-500 mt-8" data-testid="disclosure">{t.disclosure}</p>}
        {b?.purpose === 'meeting' && b.recorded && <p className="text-[12px] leading-relaxed text-slate-500 mt-8" data-testid="disclosure">{M[lang].disclosure}</p>}
      </div>
    </div>
  );

  if (gone) return shell(<p className="text-base text-slate-700" data-testid="book-gone">{t.gone}</p>);
  if (!b) return shell(<p className="text-sm text-slate-500">{t.loading}</p>);

  const meeting = b.purpose === 'meeting';
  const m = M[lang];
  if (b.state === 'closed') return shell(<p className="text-base text-slate-700">{meeting ? m.cancelled : t.cancelled}</p>);

  const names = (b.team ?? []).join(lang === 'ar' ? '، ' : ', ');
  const header = (
    <>
      <h1 className="text-2xl font-semibold leading-snug">{b.state === 'booked' && !moving ? (meeting ? m.booked : t.booked) : (meeting ? m.title : t.title)}</h1>
      {meeting && b.title && <p className="text-base text-slate-800 mt-1" data-testid="meeting-title"><bdi>{b.title}</bdi></p>}
      <p className="text-sm text-slate-600 mt-1">{meeting && names ? m.with(names) : t.with(b.organizer)}</p>
      <p className="text-sm text-slate-600"><bdi>{b.clinic}</bdi></p>
      <p className="text-sm text-slate-600">{meeting && b.with_meet === false && b.location ? m.inPerson(b.minutes, b.location) : t.minutes(b.minutes)}</p>
      {b.scripts.length > 0 && (
        <section className="mt-5 rounded-xl border border-slate-200 bg-white p-4" data-testid="agenda">
          <div className="text-[11px] uppercase tracking-wide text-slate-500 mb-2">{meeting ? m.agenda : t.scripts}</div>
          <ul className="space-y-1 text-sm list-disc ps-5">{b.scripts.map((s) => <li key={s} className="[overflow-wrap:anywhere]"><bdi>{s}</bdi></li>)}</ul>
          {!meeting && <p className="text-[12px] text-slate-500 mt-2">{t.readScripts}</p>}
        </section>
      )}
    </>
  );

  if (b.state === 'booked' && !moving) {
    return shell(
      <>
        {header}
        <section className="mt-5 rounded-xl border border-emerald-200 bg-emerald-50 p-4" data-testid="booked">
          <div className="text-lg font-medium">{b.starts_at && full(b.starts_at)}</div>
          {!sameZone && b.starts_at && <div className="text-sm text-slate-600">{t.yours}: {time(b.starts_at, localTz)}</div>}
          {b.meet_uri && <a href={b.meet_uri} className="inline-block mt-3 rounded-lg bg-sky-900 px-4 py-2 text-sm font-medium text-white">{t.join}</a>}
          <p className="text-sm text-slate-600 mt-3">{t.invite}</p>
        </section>
        {b.can_change && !confirmCancel && (
          <div className="flex flex-wrap gap-3 mt-4">
            <button type="button" className="rounded-lg border border-slate-300 px-4 py-2 text-sm" onClick={() => { setMoving(true); setSlot(null); post<Booking>('lookup', { token }).then((r) => r.ok && setB({ ...(r.data as Booking), state: 'open' })); }}>{t.move}</button>
            <button type="button" className="rounded-lg border border-red-200 px-4 py-2 text-sm text-red-700" onClick={() => setConfirmCancel(true)} data-testid="cancel-open">{meeting ? m.cancel : t.cancel}</button>
          </div>
        )}
        {!b.can_change && b.state === 'booked' && (
          <p className="text-sm text-slate-600 mt-4" data-testid="too-late">{R[lang].late(b.change_cutoff_hours ?? 2)}</p>
        )}
        {confirmCancel && (
          <div className="mt-4 rounded-xl border border-red-200 bg-red-50 p-4">
            <p className="text-sm">{meeting ? m.sure : t.sure}</p>
            <label className="block text-sm mt-3">
              <span className="block mb-1">{R[lang].why}</span>
              <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2} maxLength={500} placeholder={R[lang].whyPh}
                className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm" data-testid="cancel-reason" />
            </label>
            <div className="flex gap-3 mt-3">
              <button type="button" disabled={busy || reason.trim().length < 3} className="rounded-lg bg-red-700 px-4 py-2 text-sm text-white disabled:opacity-50" data-testid="cancel-yes"
                onClick={async () => { const r = await act<Booking>('cancel', { reason: reason.trim() }); if (r) setB({ ...r, state: 'closed' }); }}>{meeting ? m.yes : t.yes}</button>
              <button type="button" className="rounded-lg border border-slate-300 px-4 py-2 text-sm" onClick={() => setConfirmCancel(false)}>{t.back}</button>
            </div>
          </div>
        )}
        {error && <p className="text-sm text-red-700 mt-3">{error}</p>}
      </>,
    );
  }

  return shell(
    <>
      {header}
      {!moving && b.invitees.length > 1 && (
        <section className="mt-5">
          <div className="text-sm font-medium mb-2">{t.who}</div>
          <div className="flex flex-col gap-2">
            {b.invitees.map((i) => (
              <label key={i.index} className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm">
                <input type="radio" name="who" checked={who === i.index} onChange={() => setWho(i.index)} />
                <span dir="ltr">{i.label}</span>
              </label>
            ))}
          </div>
        </section>
      )}
      <section className="mt-5">
        <div className="text-sm font-medium mb-2">{t.pick}</div>
        {days.length === 0 && <p className="text-sm text-slate-600">{t.noSlots}</p>}
        {/* One day at a time: nine days of half-hour slots in a single list is a very
            long scroll on a phone. */}
        {days.length > 0 && (
          <div className="flex gap-2 overflow-x-auto pb-2 -mx-1 px-1" data-testid="days">
            {days.map((d, i) => (
              <button key={d[0]} type="button" onClick={() => { setDay(i); setSlot(null); }} aria-pressed={day === i}
                className={`shrink-0 rounded-lg border px-3 py-2 text-sm text-center min-w-[84px] ${day === i ? 'border-sky-900 bg-sky-900 text-white' : 'border-slate-200 bg-white'}`}>
                <span className="block text-[12px]">{new Date(d[0]).toLocaleDateString(loc, { timeZone: 'Asia/Dubai', weekday: 'short' })}</span>
                <span className="block font-medium tabular-nums">{new Date(d[0]).toLocaleDateString(loc, { timeZone: 'Asia/Dubai', day: 'numeric', month: 'short' })}</span>
              </button>
            ))}
          </div>
        )}
        <div className="mt-3" data-testid="slots">
          {days[day] && (
            <>
              <div className="text-[13px] text-slate-600 mb-1.5">{dayLabel(days[day][0])}</div>
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                {days[day].map((s) => (
                  <button key={s} type="button" onClick={() => setSlot(s)} data-testid="slot"
                    className={`rounded-lg border px-2 py-2 text-sm tabular-nums ${slot === s ? 'border-sky-900 bg-sky-900 text-white' : 'border-slate-200 bg-white hover:border-sky-700'}`}>
                    {time(s)}
                    {!sameZone && <span className="block text-[10px] opacity-75">{time(s, localTz)}</span>}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      </section>
      {slot && (
        <div className="sticky bottom-0 mt-5 bg-slate-50 py-3" style={{ paddingBottom: 'calc(0.75rem + env(safe-area-inset-bottom, 0px))' }}>
          <button type="button" disabled={busy || (!moving && who === null)} data-testid="confirm"
            className="w-full rounded-lg bg-sky-900 px-4 py-3 text-base font-medium text-white disabled:opacity-50"
            onClick={async () => {
              const r = moving ? await act<Booking>('reschedule', { start: slot }) : await act<Booking>('confirm', { start: slot, invitee: who });
              if (r) { setB(r); setMoving(false); setSlot(null); }
            }}>
            {t.confirm}: {full(slot)}
          </button>
        </div>
      )}
      {error && <p className="text-sm text-red-700 mt-3">{error}</p>}
      {!moving && (
        <section className="mt-6">
          {b.suggested ? <p className="text-sm text-slate-600">{t.thanks}</p> : asking ? (
            <div className="space-y-2">
              <textarea value={suggest} onChange={(e) => setSuggest(e.target.value)} rows={2} maxLength={500} placeholder={t.suggestPh}
                className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm" />
              <button type="button" disabled={busy || suggest.trim().length < 2} className="rounded-lg border border-slate-300 px-4 py-2 text-sm"
                onClick={async () => { const r = await act<Booking>('suggest', { text: suggest.trim() }); if (r) setB(r); }}>{t.send}</button>
            </div>
          ) : (
            <button type="button" className="text-sm text-sky-800 underline" onClick={() => setAsking(true)} data-testid="none-work">{t.none}</button>
          )}
        </section>
      )}
    </>,
  );
}
